import { Model as BaseModel } from '@/models/Model';
import JsonApiResponse from '@/models/JsonApiResponse';
import {
  InfiniteData,
  QueryKey,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useCallback } from 'react';

type T<
  Model,
  TError = unknown,
  TQueryKey extends QueryKey = QueryKey,
> = UseQueryOptions<
  JsonApiResponse<Model>,
  TError,
  JsonApiResponse<Model>,
  TQueryKey
>;

type Position = 'start' | 'end';

type AddManualQueryOptions = {
  position: Position;
};

export function updateInfinityPages<Model extends BaseModel>(
  listParents: InfiniteData<JsonApiResponse<Model>>,
  area: Partial<Model>,
  newId?: number,
) {
  for (let i = 0; i < listParents.pages.length; i++) {
    listParents.pages[i].results = listParents.pages[i].results.map(existing =>
      existing.id === area.id
        ? { ...existing, ...area, id: newId ?? existing.id }
        : existing,
    );
  }
  return listParents;
}

const useManualQuery = <
  Model extends BaseModel,
  TError = unknown,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: T<Model, TError, TQueryKey>,
) => {
  const queryClient = useQueryClient();

  const queryKey = options.queryKey as QueryKey;

  const add = useCallback(
    (area: Model, addOptions?: AddManualQueryOptions) => {
      const listParents =
        queryClient.getQueryData<InfiniteData<JsonApiResponse<Model>>>(
          queryKey,
        );

      const results = listParents?.pages.at(0)?.results;

      if (addOptions?.position === 'end') results?.push(area);
      else results?.unshift(area);

      queryClient.setQueryData(queryKey, listParents);
    },
    [queryClient, queryKey],
  );

  const update = useCallback(
    (area: Partial<Model>, newId?: number) => {
      let listParents =
        queryClient.getQueryData<InfiniteData<JsonApiResponse<Model>>>(
          queryKey,
        );
      if (!listParents) return;

      listParents = updateInfinityPages(listParents, area, newId);

      queryClient.setQueryData(queryKey, listParents);
    },
    [queryClient, queryKey],
  );

  const remove = useCallback(
    (areaId: number) => {
      const listParents =
        queryClient.getQueryData<InfiniteData<JsonApiResponse<Model>>>(
          queryKey,
        );
      if (!listParents) return;
      for (let i = 0; i < listParents.pages.length; i++) {
        if (listParents.pages[i].results.find(a => a.id === areaId)) {
          listParents.pages[i].results = listParents.pages[i].results.filter(
            a => a.id !== areaId,
          );
          break;
        }
      }
      queryClient.setQueryData(queryKey, listParents);
    },
    [queryClient, queryKey],
  );

  const addItem = useCallback(
    (area: Model) => {
      const listParents =
        queryClient.getQueryData<JsonApiResponse<Model>>(queryKey);

      if (!listParents) return;

      listParents.results.push(area);

      queryClient.setQueryData(queryKey, listParents);
    },
    [queryClient, queryKey],
  );

  const updateItem = useCallback(
    (area: Model) => {
      const listParents =
        queryClient.getQueryData<JsonApiResponse<Model>>(queryKey);

      if (!listParents) return;

      listParents.results = listParents.results.map(existing =>
        existing.id === area.id ? { ...existing, ...area } : existing,
      );

      queryClient.setQueryData(queryKey, listParents);
    },
    [queryClient, queryKey],
  );

  const removeItem = useCallback(
    (areaId: number) => {
      const listParents =
        queryClient.getQueryData<JsonApiResponse<Model>>(queryKey);

      if (!listParents) return;

      listParents.results = listParents.results.filter(
        existing => existing.id !== areaId,
      );

      queryClient.setQueryData(queryKey, listParents);
    },
    [queryClient, queryKey],
  );

  return {
    add,
    update,
    remove,
    addItem,
    updateItem,
    removeItem,
  };
};

export default useManualQuery;
